import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from "react";
import axios from "../api/axios";
import { User } from "./types";
import { AxiosResponse } from "axios";

interface AuthContextType {
  user: User | null;
  setUser: React.Dispatch<React.SetStateAction<User | null>>;
  loginWithEmailPassword: (
    email: string,
    password: string
  ) => Promise<AxiosResponse<any, any> | undefined>;
  logout: () => Promise<void>;
  refreshToken: () => Promise<string | null>;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | null>(null);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initializeAuth = async () => {
      setLoading(true);
      const user = localStorage.getItem("WA-auth");
      if (user?.trim()) {
        setUser(JSON.parse(user));
      } else {
        setUser(null);
      }
      setLoading(false);
    };

    initializeAuth();
  }, []);

  const refreshToken = async (): Promise<string | null> => {
    try {
      const response = await axios.get("/auth/refresh", {
        headers: { "Content-Type": "application/json" },
        withCredentials: true,
      });
      if (response?.data?.data?.accessToken) {
        setUser(response.data.data);
        return response.data.data.accessToken;
      } else {
        throw new Error("No access token in refresh response");
      }
    } catch (err) {
      setUser(null);
      localStorage.removeItem("persistant");
      return null;
    }
  };

  const loginWithEmailPassword = async (email: string, password: string) => {
    try {
      setLoading(true);
      const response = await axios.postForm(
        "/auth/admin/login",
        { username: email, password },
        {
          headers: { "Content-Type": "application/json" },
        }
      );

      if (response?.data?.data?.access_token) {
        const user = {
          access_token: response.data.data.access_token,
          ...response.data.data.user,
        };
        setUser(user);
        localStorage.setItem("WA-auth", JSON.stringify(user));
      } else {
        return response;
      }

      return response;
    } catch (err) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      setLoading(true);
      // await axios.get("/auth/logout");
      setUser(null);
      localStorage.removeItem("WA-auth");
    } catch (err) {
    } finally {
      setLoading(false);
    }
  };

  const value: AuthContextType = {
    user,
    loginWithEmailPassword,
    logout,
    refreshToken,
    loading,
    setUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
